import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import StockTable from "../../components/order/stock/StockTable";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { DEFAULT_PROJECT_REQUEST_ORDER_TYPES } from "../../../../../models/Project/OrderType";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { listingStatusTabsStyles } from "../../../../../styles/listingStatusTabsSx";
import { StockCheckRequestStatus } from "../../../../../models/Stock/Stock";
import { StockCheckListTab } from "../../../stores/StockStore";

const STOCK_CHECK_TABS: { id: StockCheckListTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
];

type StockCheckListParams = {
  page_no: number;
  limit: number;
  tab: StockCheckListTab;
  search_text: string;
  order_types: FilterValue[];
  applied_search_text: string;
  applied_order_types: string;
};

const StockCheckRequestsPage = () => {
  const theme = useTheme();
  const { stockStore, userStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState<StockCheckListParams>(() =>
    readStockCheckParams(searchParams),
  );

  const canView = userStore.hasPermission(EmployeeRoles.VIEW_ORDER);

  const { tab, page_no, limit, applied_search_text, applied_order_types } =
    params;

  useEffect(() => {
    const next = writeStockCheckQuery({
      tab,
      page_no,
      limit,
      applied_search_text,
      applied_order_types,
    });
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [
    tab,
    page_no,
    limit,
    applied_search_text,
    applied_order_types,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!canView) return;
    const search = applied_search_text.trim();
    const orderTypeIds = applied_order_types
      ? applied_order_types.split(",").map(Number)
      : undefined;
    void stockStore.loadStockList({
      page_no,
      limit,
      sortBy: "created_date",
      status: statusForTab(tab),
      order_id: search ? Number(search) : undefined,
      order_type_ids: orderTypeIds,
    });
  }, [
    canView,
    stockStore,
    page_no,
    limit,
    tab,
    applied_search_text,
    applied_order_types,
  ]);

  const applyFilters = () => {
    const search = params.search_text.trim();
    if (search && !/^\d+$/.test(search)) {
      stockStore.setListError("Enter a request id or order id.");
      return;
    }
    stockStore.setListError(null);
    setParams((prev) => ({
      ...prev,
      search_text: search,
      page_no: 1,
      applied_search_text: search,
      applied_order_types: orderTypeIdsKey(prev.order_types),
    }));
  };

  const refreshList = async () => {
    try {
      await stockStore.refreshData();
    } catch (error: any) {
      stockStore.setListError(
        error?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    }
  };

  const clearAll = () => {
    stockStore.setListError(null);
    setParams((prev) => ({
      ...prev,
      search_text: "",
      order_types: [],
      applied_search_text: "",
      applied_order_types: "",
      page_no: 1,
    }));
  };

  const shell = (content: React.ReactNode) => (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          mt: "0.6rem",
          px: { xs: 2, md: 4 },
          py: 2,
          borderRadius: "4px",
          bgcolor: theme.palette.customColors.white,
          boxShadow:
            "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
          minHeight: { xs: "60vh", md: "calc(100vh - 110px)" },
        }}
      >
        {content}
      </Paper>
    </Grid2>
  );

  if (!canView) {
    return shell(<PermissionBlockLabel />);
  }

  return shell(
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <CustomTypography variant="heading3">
            Stock Check Request List
          </CustomTypography>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors.greyTextColor}
          >
            Filter by request id, order id, or order types.
          </CustomTypography>
        </Box>
        <AppButton
          label={stockStore.listLoading ? "Refreshing..." : "Refresh"}
          variant="secondary"
          startIcon={<Refresh />}
          onClick={() => void refreshList()}
          disabled={stockStore.listLoading}
          customStyles={{ minHeight: 36, height: 36, px: 2 }}
        />
      </Box>
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: theme.palette.customColors.white,
        }}
      >
        <Box
          onClick={() => stockStore.toggleFilters()}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 0.5,
            cursor: "pointer",
            borderBottom: stockStore.filtersExpanded ? "1px solid" : "none",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CustomTypography variant="heading6">Filters</CustomTypography>
          <IconButton size="small">
            {stockStore.filtersExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={stockStore.filtersExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 1.5,
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ flex: { md: 1 }, minWidth: 0, width: "100%" }}>
              <FormTextField
                id="stock-check-id"
                placeholder="Request id or order id"
                type="search"
                variant="outlined"
                marginBottom={0}
                value={params.search_text}
                onChange={(_, value) =>
                  setParams((prev) => ({
                    ...prev,
                    search_text: value || "",
                  }))
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyFilters();
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { height: 40 },
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
              <AppSearchDropdownMulti
                title="Order type"
                isMultiSelect
                local
                inputHeight="40px"
                fontSize="12px"
                value={params.order_types}
                list={DEFAULT_PROJECT_REQUEST_ORDER_TYPES}
                handleSelect={(items) =>
                  setParams((prev) => ({
                    ...prev,
                    order_types: items,
                  }))
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                flexShrink: 0,
                width: { xs: "100%", md: "auto" },
              }}
            >
              <AppButton
                variant="primary"
                label="Apply"
                onClick={applyFilters}
                customStyles={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 120 },
                  height: 40,
                }}
              />
              <AppButton
                variant="transparentPrimary"
                label="Clear All"
                onClick={clearAll}
                customStyles={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 120 },
                  height: 40,
                }}
              />
            </Box>
          </Box>
        </Collapse>
      </Paper>
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: theme.palette.customColors.white,
        }}
      >
        <Tabs
          value={params.tab}
          onChange={(_, value: StockCheckListTab) =>
            setParams((prev) => ({ ...prev, tab: value, page_no: 1 }))
          }
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={(tabTheme) => ({ ...listingStatusTabsStyles(tabTheme), mb: 0 })}
        >
          {STOCK_CHECK_TABS.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.label.toUpperCase()}
              id={`stock-check-tab-${tab.id}`}
            />
          ))}
        </Tabs>
        {stockStore.listError && (
          <Box sx={{ px: 2, pt: 1 }}>
            <InlineFormError message={stockStore.listError} />
          </Box>
        )}
        {stockStore.listLoading ? (
          <Box
            sx={{
              width: "100%",
              minHeight: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          (!stockStore.listError || stockStore.stocks.length > 0) && (
            <StockTable
              stocks={stockStore.stocks}
              limit={params.limit}
              page_no={params.page_no}
              total_count={stockStore.total_count}
              onPageChange={(_event, page) =>
                setParams((prev) => ({ ...prev, page_no: page }))
              }
              onRowsPerPageChange={(limit) =>
                setParams((prev) => ({ ...prev, limit, page_no: 1 }))
              }
            />
          )
        )}
      </Paper>
    </>,
  );
};

function statusForTab(tab: StockCheckListTab): number | undefined {
  if (tab === "open") return StockCheckRequestStatus.OPEN;
  if (tab === "closed") return StockCheckRequestStatus.CLOSED;
  return undefined;
}

function parsePositiveInt(value: string | null, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return fallback;
  return parsed;
}

function readStockCheckParams(query: URLSearchParams): StockCheckListParams {
  const tabParam = (query.get("tab") || "open").toLowerCase();
  const tab: StockCheckListTab =
    tabParam === "all" || tabParam === "open" || tabParam === "closed"
      ? tabParam
      : "open";
  const search = (query.get("order_id") || "").trim();
  const orderTypes = readOrderTypes(query.get("order_type"));
  return {
    page_no: parsePositiveInt(query.get("page") || query.get("page_no"), 1),
    limit: parsePositiveInt(query.get("limit"), 100),
    tab,
    search_text: search,
    order_types: orderTypes,
    applied_search_text: /^\d+$/.test(search) ? search : "",
    applied_order_types: orderTypeIdsKey(orderTypes),
  };
}

function readOrderTypes(raw: string | null): FilterValue[] {
  if (!raw) return [];
  const ids = new Set(
    raw
      .split(",")
      .map((item) => item.trim())
      .filter((item) => /^\d+$/.test(item)),
  );
  return DEFAULT_PROJECT_REQUEST_ORDER_TYPES.filter((item) =>
    ids.has(String(item.id)),
  );
}

function orderTypeIdsKey(types: FilterValue[]): string {
  return types
    .map((item) => Number(item.id))
    .filter((id) => Number.isInteger(id) && id >= 1 && id <= 6)
    .filter((id, index, all) => all.indexOf(id) === index)
    .sort((left, right) => left - right)
    .join(",");
}

function writeStockCheckQuery(query: {
  tab: StockCheckListTab;
  page_no: number;
  limit: number;
  applied_search_text: string;
  applied_order_types: string;
}): URLSearchParams {
  const next = new URLSearchParams();
  next.set("tab", query.tab);
  if (query.page_no > 1) next.set("page", String(query.page_no));
  if (query.limit !== 100) next.set("limit", String(query.limit));
  if (query.applied_search_text) next.set("order_id", query.applied_search_text);
  if (query.applied_order_types) {
    next.set("order_type", query.applied_order_types);
  }
  return next;
}

export default observer(StockCheckRequestsPage);
